import { API_BASE_URL } from './config';
import { apiFetch, isNetworkError, markLoaded, renderError } from './helpers';
import type { ElectricityPricePoint, ElectricityPricesResponse } from './types';

let elChartAbortController: AbortController | null = null;

function findCheapestWindow(prices: ElectricityPricePoint[], windowSize: number, currentHour: number): { startHour: string; endHour: string; avgPrice: number } | null {
    // Only consider remaining hours of the present day.
    const remaining = prices.filter(p => {
        if (!p.isToday) return false;
        const h = parseInt(p.hour.split(':')[0], 10);
        return h >= currentHour;
    });

    if (remaining.length < windowSize) return null;

    let bestAvg = Infinity;
    let bestIdx = 0;

    for (let i = 0; i <= remaining.length - windowSize; i++) {
        let sum = 0;
        for (let j = 0; j < windowSize; j++) {
            sum += remaining[i + j].price;
        }
        const avg = sum / windowSize;
        if (avg < bestAvg) {
            bestAvg = avg;
            bestIdx = i;
        }
    }

    const startPrice = remaining[bestIdx];
    const endPrice = remaining[bestIdx + windowSize - 1];
    const endHourNum = (parseInt(endPrice.hour.split(':')[0], 10) + 1) % 24;

    return {
        startHour: startPrice.hour,
        endHour: `${String(endHourNum).padStart(2, '0')}:00`,
        avgPrice: bestAvg,
    };
}

function drawElectricityChart(canvas: HTMLCanvasElement, todayPrices: ElectricityPricePoint[], tomorrowPrices: ElectricityPricePoint[], currentHourStr: string | null): void {
    if (elChartAbortController) elChartAbortController.abort();
    elChartAbortController = new AbortController();
    const signal = elChartAbortController.signal;

    const ctx = canvas.getContext('2d');
    const allPrices = todayPrices.concat(tomorrowPrices);
    if (!ctx || allPrices.length < 2) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const padTop = 16;
    const padBottom = 24;
    const padLeft = 40;
    const padRight = 8;
    const chartW = w - padLeft - padRight;
    const chartH = h - padTop - padBottom;

    const values = allPrices.map(p => p.price);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const barWidth = chartW / allPrices.length;

    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    const gridLines = 4;
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let i = 0; i <= gridLines; i++) {
        const y = padTop + (chartH / gridLines) * i;
        ctx.beginPath();
        ctx.moveTo(padLeft, y);
        ctx.lineTo(w - padRight, y);
        ctx.stroke();
        const priceAtLine = max - (range / gridLines) * i;
        ctx.fillStyle = '#5e6470';
        ctx.fillText(`${priceAtLine.toFixed(1)}`, padLeft - 6, y);
    }

    const nowHour = currentHourStr ? parseInt(currentHourStr.split(':')[0], 10) : -1;
    const todayCount = todayPrices.length;

    for (let i = 0; i < allPrices.length; i++) {
        const p = allPrices[i];
        const barH = ((p.price - min) / range) * chartH;
        const x = padLeft + i * barWidth;
        const y = padTop + chartH - barH;

        const hourNum = parseInt(p.hour.split(':')[0], 10);
        const isCurrent = p.isToday && hourNum === nowHour;
        const isTomorrow = i >= todayCount;

        if (isCurrent) {
            ctx.fillStyle = '#8badc8';
        } else if (p.price < 0) {
            ctx.fillStyle = 'rgba(93,158,107,0.7)';
        } else if (isTomorrow) {
            ctx.fillStyle = 'rgba(139,173,200,0.25)';
        } else {
            ctx.fillStyle = 'rgba(139,173,200,0.45)';
        }

        ctx.fillRect(x + 1, y, barWidth - 2, barH);
    }

    if (tomorrowPrices.length > 0) {
        const divX = padLeft + todayCount * barWidth;
        ctx.strokeStyle = 'rgba(255,255,255,0.15)';
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(divX, padTop);
        ctx.lineTo(divX, padTop + chartH);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#5e6470';
        ctx.font = '9px Inter, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('Tomorrow', divX + 4, padTop + 10);
    }

    ctx.fillStyle = '#5e6470';
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (let i = 0; i < allPrices.length; i++) {
        const hourNum = parseInt(allPrices[i].hour.split(':')[0], 10);
        if (hourNum % 3 === 0) {
            const x = padLeft + i * barWidth + barWidth / 2;
            ctx.fillText(allPrices[i].hour, x, h - 14);
        }
    }

    const tooltip = document.getElementById('electricity-tooltip');
    if (!tooltip) return;

    canvas.addEventListener('mousemove', (e: MouseEvent) => {
        const canvasRect = canvas.getBoundingClientRect();
        const mx = e.clientX - canvasRect.left;
        const idx = Math.max(0, Math.min(allPrices.length - 1, Math.floor((mx - padLeft) / barWidth)));
        const p = allPrices[idx];
        const px = padLeft + idx * barWidth + barWidth / 2;
        const barH = ((p.price - min) / range) * chartH;
        const py = padTop + chartH - barH;
        const dayLabel = p.isToday ? 'Today' : 'Tomorrow';
        tooltip.textContent = `${dayLabel} ${p.hour}  ${p.price.toFixed(2)} c/kWh`;
        tooltip.style.left = `${px}px`;
        tooltip.style.top = `${py - 32}px`;
        tooltip.style.transform = 'translateX(-50%)';
        tooltip.classList.add('visible');
    }, { signal });

    canvas.addEventListener('mouseleave', () => {
        tooltip.classList.remove('visible');
    }, { signal });
}

export async function fetchElectricityPrices(): Promise<void> {
    const container = document.getElementById('electricity-data');
    const canvas = document.getElementById('electricity-chart') as HTMLCanvasElement | null;
    if (!container) return;

    try {
        const response = await apiFetch(`${API_BASE_URL}/electricityprices`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data: ElectricityPricesResponse = await response.json();

        if (data.currentPrice == null) {
            container.innerHTML = '<p class="electricity-error">No price data available.</p>';
            return;
        }

        const priceColor = data.currentPrice < 0 ? 'price-low' : '';
        const statsHtml = data.todayMin != null && data.todayMax != null && data.todayAverage != null
            ? `<div class="electricity-stats">
                <span><span class="electricity-stat-label">Low</span><span class="electricity-stat-value price-low">${data.todayMin.toFixed(2)}</span></span>
                <span><span class="electricity-stat-label">Avg</span><span class="electricity-stat-value">${data.todayAverage.toFixed(2)}</span></span>
                <span><span class="electricity-stat-label">High</span><span class="electricity-stat-value price-high">${data.todayMax.toFixed(2)}</span></span>
              </div>`
            : '';

        const nowHour = data.currentHour ? parseInt(data.currentHour.split(':')[0], 10) : new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Helsinki' })).getHours();
        const cheapest = findCheapestWindow(data.todayPrices, 3, nowHour);
        const cheapestHtml = cheapest
            ? `<div class="electricity-cheapest">
                <span class="electricity-cheapest-label">&#9889; Halvin 3h</span>
                <span class="electricity-cheapest-value">${cheapest.startHour}–${cheapest.endHour}</span>
                <span class="electricity-cheapest-avg">${cheapest.avgPrice.toFixed(2)} c/kWh</span>
              </div>`
            : '';

        container.innerHTML = `
            <span class="electricity-price ${priceColor}">${data.currentPrice.toFixed(2)}</span>
            <span class="electricity-unit">${data.unit} · nyt (${data.currentHour})</span>
            ${statsHtml}
            ${cheapestHtml}
            <span class="electricity-source">Source: <a href="https://sahkotin.fi" target="_blank" rel="noopener noreferrer">Nord Pool via sahkotin.fi</a></span>
        `;

        const allPrices = data.todayPrices.concat(data.tomorrowPrices);
        if (canvas && allPrices.length > 1) {
            canvas.style.display = '';
            drawElectricityChart(canvas, data.todayPrices, data.tomorrowPrices, data.currentHour);
        }

        markLoaded(container, 'electricity');
    } catch (error) {
        renderError(container, 'electricity', 'Could not load electricity prices.', error);
        if (isNetworkError(error) && container.dataset.loaded === 'true') return;
        if (canvas) canvas.style.display = 'none';
    }
}
